/**
 * Color Role - Cast Pillar
 *
 * Normaliza input sujo para uma variante de cor específica.
 *
 * @example
 * import { castColor, tryCastColor, detectColorFormat } from '@attrx/role-morphic/color/cast';
 *
 * castColor('hex', 'red');                     // '#ff0000'
 * castColor('rgb_object', '#ff0000');          // { r: 255, g: 0, b: 0 }
 * castColor('hsl_string', { r: 255, g: 0, b: 0 }); // 'hsl(0, 100%, 50%)'
 */

import type { ColorVariant, RGBA, ColorRgbObject, ColorHslObject } from "./types";
import { NAMED_COLORS } from "./types";
import {
  toBaseColor,
  fromBaseColor,
  hexToRgba,
  rgbObjectToRgba,
  rgbStringToRgba,
  hslObjectToRgba,
  hslStringToRgba,
} from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// DETECT FORMAT
// =============================================================================

/**
 * Detecta o formato de uma string de cor
 *
 * @example
 * detectColorFormat('red');           // 'named'
 * detectColorFormat('#ff0000');       // 'hex'
 * detectColorFormat('rgb(255,0,0)');  // 'rgb_string'
 * detectColorFormat('hsl(0,100%,50%)'); // 'hsl_string'
 */
export function detectColorFormat(
  value: string,
): "hex" | "rgb_string" | "hsl_string" | "named" | null {
  const trimmed = value.trim().toLowerCase();

  // Named color
  if (NAMED_COLORS[trimmed]) {
    return "named";
  }

  // Hex
  if (/^#?[0-9a-f]{3,8}$/i.test(trimmed)) {
    return "hex";
  }

  // RGB string
  if (/^rgba?\s*\(/i.test(trimmed)) {
    return "rgb_string";
  }

  // HSL string
  if (/^hsla?\s*\(/i.test(trimmed)) {
    return "hsl_string";
  }

  return null;
}

/**
 * Detecta se um objeto é RGB ou HSL
 */
function detectObjectFormat(obj: Record<string, unknown>): "rgb_object" | "hsl_object" | null {
  if (typeof obj.r === "number" && typeof obj.g === "number" && typeof obj.b === "number") {
    return "rgb_object";
  }
  if (typeof obj.h === "number" && typeof obj.s === "number" && typeof obj.l === "number") {
    return "hsl_object";
  }
  return null;
}

// =============================================================================
// PARSE INPUT
// =============================================================================

/**
 * Tenta parsear qualquer input para RGBA
 */
function parseToRgba(input: unknown): RGBA | null {
  // Já é RGBA
  if (
    typeof input === "object" &&
    input !== null &&
    "r" in input &&
    "g" in input &&
    "b" in input &&
    "a" in input
  ) {
    const rgba = input as RGBA;
    if (
      typeof rgba.r === "number" &&
      typeof rgba.g === "number" &&
      typeof rgba.b === "number" &&
      typeof rgba.a === "number"
    ) {
      return rgba;
    }
  }

  // String
  if (typeof input === "string") {
    const trimmed = input.trim();

    // Named color
    const namedColor = NAMED_COLORS[trimmed.toLowerCase()];
    if (namedColor) {
      return namedColor;
    }

    // Hex
    if (/^#?[0-9a-f]{3,8}$/i.test(trimmed)) {
      try {
        return hexToRgba(trimmed);
      } catch {
        return null;
      }
    }

    // RGB string
    if (/^rgba?\s*\(/i.test(trimmed)) {
      try {
        return rgbStringToRgba(trimmed);
      } catch {
        return null;
      }
    }

    // HSL string
    if (/^hsla?\s*\(/i.test(trimmed)) {
      try {
        return hslStringToRgba(trimmed);
      } catch {
        return null;
      }
    }

    return null;
  }

  // Object
  if (typeof input === "object" && input !== null) {
    const format = detectObjectFormat(input as Record<string, unknown>);

    if (format === "rgb_object") {
      try {
        return rgbObjectToRgba(input as ColorRgbObject);
      } catch {
        return null;
      }
    }

    if (format === "hsl_object") {
      try {
        return hslObjectToRgba(input as ColorHslObject);
      } catch {
        return null;
      }
    }
  }

  return null;
}

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de qualquer input para uma variante de cor
 *
 * @param variant - Variante de destino
 * @param input - Valor de entrada (string, object, etc)
 * @returns Valor na variante de destino ou null se não puder converter
 *
 * @example
 * castColor('hex', 'red');                     // '#ff0000'
 * castColor('rgb_object', '#ff0000');          // { r: 255, g: 0, b: 0 }
 * castColor('hsl_string', { r: 255, g: 0, b: 0 }); // 'hsl(0, 100%, 50%)'
 * castColor('hex', 'invalid');                 // null
 */
export function castColor<T = unknown>(variant: ColorVariant, input: unknown): T | null {
  const rgba = parseToRgba(input);
  if (!rgba) {
    return null;
  }

  try {
    return fromBaseColor(variant, rgba) as T;
  } catch {
    return null;
  }
}

/**
 * Versão safe de castColor que retorna Result
 */
export function tryCastColor<T = unknown>(
  variant: ColorVariant,
  input: unknown,
): Result<T> {
  const result = castColor<T>(variant, input);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to color variant "${variant}"`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Converte hex para RGB object
 */
export function hexToRgb(hex: string): ColorRgbObject | null {
  return castColor<ColorRgbObject>("rgb_object", hex);
}

/**
 * Converte RGB object para hex
 */
export function rgbToHex(rgb: ColorRgbObject): string | null {
  return castColor<string>("hex", rgb);
}

/**
 * Converte hex para HSL object
 */
export function hexToHsl(hex: string): ColorHslObject | null {
  return castColor<ColorHslObject>("hsl_object", hex);
}

/**
 * Converte HSL object para hex
 */
export function hslToHex(hsl: ColorHslObject): string | null {
  return castColor<string>("hex", hsl);
}

/**
 * Converte RGB string para hex
 */
export function rgbStringToHex(rgb: string): string | null {
  return castColor<string>("hex", rgb);
}

/**
 * Converte hex para RGB string
 */
export function hexToRgbString(hex: string): string | null {
  return castColor<string>("rgb_string", hex);
}

/**
 * Converte HSL string para hex
 */
export function hslStringToHex(hsl: string): string | null {
  return castColor<string>("hex", hsl);
}

/**
 * Converte hex para HSL string
 */
export function hexToHslString(hex: string): string | null {
  return castColor<string>("hsl_string", hex);
}

/**
 * Verifica se uma string é uma cor válida (qualquer formato)
 */
export function isColorString(value: string): boolean {
  return parseToRgba(value) !== null;
}

/**
 * Alias para isColorString - verifica se valor é uma cor válida
 * (Mantido para compatibilidade com API anterior)
 */
export function isValidColor(value: string): boolean {
  return isColorString(value);
}
